/**
 * @title The add dialog
 * @description The dialog shown when the user adds a whole sale item for tradein
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/mainpage-additem.png
 */
import * as React from "react";
import {FC, useEffect, useState} from "react";
import {Box, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Slide, useMediaQuery, useTheme} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {useRecoilValue} from "recoil";
import {AppDataAtom} from "../../../../providers/access/ApplicationDataProvider";
import {Category} from "./Category";
import {GlobalLayout, StyledTypography} from "../../../../providers/layout/LayoutProvider";
import {ImageUpload} from "../upload/ImageUpload";
import {ArrowIconBack, CloseIcon} from "../../../../svg/SvgIcons";
import {WholeSaleCategory} from "../../../../Queries";
import {LocaleAtom, LocalizationsAtom} from "../../../../providers/localization/LocalizationProvider";
import clsx from "clsx";
import {AddButton} from "../AddButton";
import {Analytics, AnalyticsEvent} from "../../../../analytics/Analytics";
import DialogActions from "@mui/material/DialogActions";
import {ProductSearch} from "./ProductSearch";
import {SearchProduct, SearchProductSize} from "./ProductSearchApi";

export type SpecificProduct = {
   product: SearchProduct
   size?: SearchProductSize
}

type AddItemDialogProps = {
   onHide: (cancel: boolean) => void
   onCreate: (category: WholeSaleCategory, imageId?: string, advanced?: SpecificProduct) => Promise<void>
   enableProductSearch: boolean
}

export const AddItemDialog: FC<AddItemDialogProps> = ({onHide, onCreate, enableProductSearch}) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const locale = useRecoilValue(LocaleAtom)
   const [selectedCategory, setSelectedCategory] = useState<WholeSaleCategory | undefined>()
   const [imageId, setImageId] = useState<string | undefined>();
   const theme = useTheme();
   const globalLayout = useRecoilValue(GlobalLayout);
   const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

   const [searchState, setSearchState] = useState<'product' | 'category'>(
      enableProductSearch ? 'product' : 'category'
   )
   const [adding, setAdding] = useState<boolean>(false)
   const appData = useRecoilValue(AppDataAtom);

   const classes = useAddItemDialogStyles();

   const onSelectCategory = (category: WholeSaleCategory) => {
      Analytics.event(AnalyticsEvent.trackItemCategory, {
         wholeSaleCategory: category.name
      });
      setSelectedCategory(category);
   }

   const onUpdateImage = (imageId: string | undefined) => {
      Analytics.event(AnalyticsEvent.trackItemImage, {
         wholeSaleImageId: imageId
      });
      setImageId(imageId)
   }

   const onAddItem = async () => {
      setAdding(true);
      await onCreate(selectedCategory!, imageId!)
      setAdding(false);
   }

   const onSelectProduct = async (product: SearchProduct, size: SearchProductSize) => {
      setAdding(true);
      await onCreate(selectedCategory!, undefined, {
         product, size
      })
      setAdding(false);
   }

   useEffect(() => {
      Analytics.event(AnalyticsEvent.trackItemDialog);
   }, [])

   if (adding) {
      return <Dialog open={true} className={clsx(classes.dialog, 'c2s-dialog')} maxWidth={"md"}
                     fullWidth={true}
                     fullScreen={fullScreen} PaperProps={{
         sx: {
            '@media only screen and (min-width: 800px)': {
               minHeight: '70vh',
               maxHeight: '70vh',
               overflow: 'hidden'
            }
         }
      }}>
         <DialogContent sx={{
            padding: '20px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#e0e0e0'
         }}>
            <CircularProgress size={100} sx={{
               color: '#000000'
            }}/>
         </DialogContent>
      </Dialog>
   }

   const sections: string[] = [""];
   const sectionCategories = new Map<string, WholeSaleCategory[]>();
   for (const category of appData.categories) {
      const localizedValue = category.sectionLocalized?.find(loc => loc?.locale === locale);
      const section = localizedValue?.value || category.section;
      if (section && !sections.includes(section)) {
         sections.push(section);
      }
      const effSection = section || ""
      if (!sectionCategories.has(effSection)) {
         sectionCategories.set(effSection, []);
      }
      sectionCategories.get(effSection)?.push(category);
   }

   const effHide = (cancel: boolean) => {
      if (enableProductSearch) {
         if (searchState === 'category') {
            if (selectedCategory) {
               setSelectedCategory(undefined);
            } else {
               setSearchState('product')
            }
            return
         }
      }
      if (selectedCategory) {
         setSelectedCategory(undefined);
      } else {
         onHide(cancel);
      }
   }

   const NavigationButton = () => {
      const showBack = (enableProductSearch && searchState === 'category') || (!enableProductSearch && selectedCategory)
      return <IconButton size={"medium"} onClick={() => effHide(true)}>
         {showBack && <ArrowIconBack iconcolor={globalLayout.widgets?.actionItemBorderColor || '#FF5376'}/>}
         {!showBack && <CloseIcon iconcolor={globalLayout.widgets?.actionItemBorderColor || '#FF5376'}/>}
      </IconButton>
   }

   return <Dialog open={true} className={clsx(classes.dialog, 'c2s-dialog')} maxWidth={"md"}
                  fullWidth={true}
                  fullScreen={fullScreen} PaperProps={{
      sx: {
         '@media only screen and (min-width: 800px)': {
            minHeight: '70vh',
            maxHeight: '70vh',
            overflow: 'hidden'
         }
      }
   }}>
      {searchState === 'product' &&
         <DialogTitle className={clsx(classes.dialogTitle, "c2s-dialogtitle")} component={"div"}
                      sx={{spacing: '10px', backgroundColor: '#f8f8f8'}}>
            <StyledTypography type={"main"} variant={"h1"}
                              className={"c2s-dialogtitle-h1"}
                              dangerouslySetInnerHTML={{__html: localizations.wholeSaleSearchTitle}}/>
            <StyledTypography type={"main"} variant={"body1"}
                              sx={{whiteSpace: 'normal !important'}}
                              className={"c2s-dialogtitle-body"}
                              dangerouslySetInnerHTML={{__html: localizations.wholeSaleSearchSubTitle}}
            />
            <div className={classes.closeDialog}>
               <NavigationButton/>
            </div>
         </DialogTitle>
      }
      {searchState === 'category' &&
         <DialogTitle className={classes.dialogTitle} component={"div"} sx={{textAlign: 'center'}}>
            <StyledTypography type={"main"} variant={"h1"} className={"c2s-additem-title"}
                              dangerouslySetInnerHTML={{__html: localizations.wholeSaleAddTitle}}/>
            <StyledTypography type={"main"} variant={"body1"} className={"c2s-additem-subtitle"}
                              dangerouslySetInnerHTML={{__html: localizations.wholeSaleAddDescription}}/>
            <div className={classes.closeDialog}>
               <NavigationButton/>
            </div>
         </DialogTitle>
      }

      {((searchState === 'category') || !enableProductSearch) && <>
         <DialogContent className={classes.dialogContent} sx={{borderTop: '1px solid #f8f8f8', overflowY: 'scroll'}}>
            <div className={classes.dialogContainer}>
               {selectedCategory && <>
                  <Slide in={!!selectedCategory}>
                     <Box className={clsx(classes.categoryContainer, 'c2s-category-container-outer')}>
                        <Box sx={{width: '50%', display: 'flex'}}>
                           <Category key={'category_' + selectedCategory._id}
                                     wider={true} selected={true}
                                     onSelect={() => setSelectedCategory(undefined)}
                                     category={selectedCategory}
                                     showSection={sectionCategories.size > 0}
                           />
                        </Box>
                     </Box>
                  </Slide>
               </>
               }
               <Collapse in={!selectedCategory}>
                  <Box>
                     {sections.map(section => {
                        const categories = sectionCategories.get(section) || [];
                        if (categories.length === 0) return null
                        return <Box sx={{textAlign: 'center'}}
                                    key={'section_' + section}>
                           {(section && categories.length > 0) &&
                              <StyledTypography sx={{paddingTop: '15px', fontWeight: 'bold'}} type={"main"}
                                                variant={"h2"}>{section}</StyledTypography>
                           }
                           <div style={{marginTop: section ? '5px' : 'inherit'}}
                                className={clsx(classes.categoryContainer, 'c2s-category-container-outer')}>
                              {categories.map(cat => {
                                 let wider = appData.categories.length === 4 || appData.categories.length === 7;
                                 return <Category key={'category_' + cat._id}
                                                  wider={wider} selected={false}
                                                  onSelect={onSelectCategory}
                                                  category={cat}/>
                              })}
                           </div>
                        </Box>
                     })}
                  </Box>
               </Collapse>
            </div>
            {selectedCategory &&
               <div className={classes.imageUploadContainer}>
                  <ImageUpload imageId={imageId} hidden={false} enabled={!!selectedCategory} onUpdate={onUpdateImage}/>
               </div>
            }
            {!selectedCategory &&
               <div className={classes.additionalTextContainer}>
                  <div className={classes.additionalTextContainerInner}>
                     <StyledTypography type={"main"} variant={"h2"}
                                       style={{textAlign: 'center'}}
                                       dangerouslySetInnerHTML={{__html: localizations.wholeSaleAdditionalHeader}}/>
                     <StyledTypography type={"main"} variant={"body1"}
                                       style={{textAlign: 'center'}}
                                       dangerouslySetInnerHTML={{__html: localizations.wholeSaleAdditionalDescription}}/>
                  </div>
               </div>
            }
         </DialogContent>
         {(selectedCategory && imageId) && <DialogActions sx={{minHeight: '5px'}}>
            <div className={classes.buttonContainer}>
               <Box sx={{textAlign: 'center', opacity: (selectedCategory && imageId) ? 1 : 0.4}}>
                  <AddButton isEmpty={true} onClick={onAddItem} disabled={!selectedCategory || !imageId}>
                     {localizations.wholeSaleAddItem}
                  </AddButton>
               </Box>
            </div>
         </DialogActions>}
      </>
      }
      {(searchState === 'product' && enableProductSearch) &&
         <ProductSearch onSelect={(p, s) => onSelectProduct(p, s)} onSwitch={() => setSearchState('category')}/>
      }
   </Dialog>
}

const useAddItemDialogStyles = makeStyles({
   dialog: {
      backgroundColor: 'inherit'
   },
   dialogTitle: {
      textAlign: 'center'
   },
   dialogContent: {
      padding: '0px'
   },
   imageUploadContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: '30px'
   },
   additionalTextContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: '30px'
   },
   additionalTextContainerInner: {
      maxWidth: '80%',
      textAlign: 'center'
   },
   dialogContainer: {
      padding: '0 50px 0 50px',
      position: 'relative'
   },
   categoryContainer: {
      display: 'flex',
      gap: '20px',
      flexDirection: 'row',
      alignItems: 'top',
      paddingLeft: '0px',
      paddingRight: '0px',
      marginTop: '20px',
      flexWrap: 'wrap',
      justifyContent: 'center'
   },
   buttonContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-evenly',
      marginTop: '10px',
      marginBottom: '10px',
      width: '100%'
   },
   closeDialog: {
      position: 'absolute',
      right: '20px',
      top: '12px'
   },
   backDialog: {
      position: 'absolute',
      left: '45px',
      top: '15px'
   },
   '@media only screen and (max-width: 800px)': {
      dialogContainer: {
         padding: '0 15px 0 15px'
      },
      backDialog: {
         left: '15px',
      },
      closeDialog: {
         right: '30px'
      }
   },
})
